import Autolinker from 'autolinker'
import isObject from 'lodash/isObject'
import { computed, Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useSystemMessage } from '@shared/composables/use-system-message'
import { AI_MESSAGE_ATTRIBUTE } from '@shared/constants/ai-messages'
import { IMessage, INonQliqMessage } from '@shared/models/proto/base/messages'
import { getDateTimeString } from '@shared/utils/helpers/date-formatter'
import { generatePhoneLink } from '@shared/utils/helpers/generate-phone-link'
import regExp from '@shared/utils/helpers/regular-expressions'

import { MessageType } from '@protos/chat/message_type_pb'
import { UserType } from '@protos/user_type_pb'

import { Marked } from '@ts-stack/markdown'

/**
 * diagram - https://gitlab.com/qliqdev/qliqweb-vue/-/blob/dev/docs/marmaid/shared/use-message-text.md?ref_type=heads
 * */

export const useMessageText = (
  message: Ref<IMessage | INonQliqMessage>,
  isMe?: Ref<boolean>
) => {
  const { t } = useI18n()
  const { isTacSystemMessage, tacSystemMessageText } = useSystemMessage(
    message.value.messageType
  )

  const isBotMessage = computed(
    () => (message.value as IMessage)?.sender?.type === UserType.BOT
  )

  const isVisitor = computed(
    () => (message.value as IMessage)?.sender?.type === UserType.VISITOR
  )

  const isRecalled = computed(
    () => (message.value as IMessage)?.actualStatus?.recalledFromAt
  )

  const messageTime = computed(() => {
    const date =
      (message.value as IMessage).actualStatus?.deliveredTime ||
      (message.value as INonQliqMessage)?.deliveredTime ||
      message.value.createdAt

    const short = getDateTimeString({
      date,
      formatPattern: 'hh:mm',
      fallbackValue: '',
    })

    const long = getDateTimeString({
      date,
      formatPattern: 'hh:mm A',
      fallbackValue: '',
    })

    return {
      short,
      long,
    }
  })

  const removeDoubleSlashFromN = (input: string): string =>
    input.replace(/\\n/g, '\n')

  const generateMessageText = (text: string) => {
    if (isTacSystemMessage.value) {
      return tacSystemMessageText.value
    }

    const parsedTextWithLink = Autolinker.link(text)
    const isPhone = regExp.usNumberRegExp.test(text)
    const isLink = regExp.links.test(text)

    const getMessage = (messageText: string) => {
      if (isRecalled.value && !isMe?.value) {
        return `<i>${t('chat.messages.recalledBySender')}</i>`
      }

      if (isPhone && isLink) {
        const text = Autolinker.link(generatePhoneLink(messageText))
        return isBotMessage.value ? Marked.parse(text) : text
      }

      if (isPhone) {
        const text = generatePhoneLink(messageText)
        return isBotMessage.value ? Marked.parse(text) : text
      }

      if (isLink) {
        return isBotMessage.value
          ? Marked.parse(parsedTextWithLink)
          : parsedTextWithLink
      }

      if (isBotMessage.value) {
        return Marked.parse(parsedTextWithLink)
      }

      return messageText
    }

    try {
      const parsedText = JSON.parse(text)

      if (!parsedText.text && parsedText.text !== '') {
        if (parsedText && isObject(parsedText)) {
          // eslint-disable-next-line no-console
          console.error(
            'Invalid json structure in `Message` interface . Expect text as a string got object.'
          )
          return Marked.parse(``)
        }
        throw Error('Invalid json structure. Cannot parse message text.')
      }

      return getMessage(parsedText.text)
    } catch (e) {
      return getMessage(text)
    }
  }

  const messageText = computed(() => {
    const { text } = message.value

    if (isTacSystemMessage.value) {
      return tacSystemMessageText.value
    }

    return generateMessageText(removeDoubleSlashFromN(text))
  })

  const isMessageToAi = computed(() =>
    messageText.value.includes(`data-message="${AI_MESSAGE_ATTRIBUTE}"`)
  )

  const isSanitized = computed(
    () =>
      messageText.value.includes('<a href=') ||
      isBotMessage.value ||
      isMessageToAi.value ||
      (isRecalled.value && !isMe?.value)
  )

  const isShowMessage = (params: {
    isAttachmentInitial: boolean
    isAttachmentLoaded: boolean
  }) => {
    const { isAttachmentInitial, isAttachmentLoaded } = params
    const isSelfSchedulerFromBot =
      message.value.messageType === MessageType.SCHEDULER && isBotMessage.value

    const isTacPresentedMessage =
      message.value.messageType === MessageType.TAC_PRESENTED

    return (
      !isTacPresentedMessage &&
      (!!messageText.value ||
        isAttachmentInitial ||
        isAttachmentLoaded ||
        !isSelfSchedulerFromBot)
    )
  }

  return {
    messageText,
    messageTime,
    isBotMessage,
    isVisitor,
    isMessageToAi,
    isSanitized,
    isShowMessage,
    isRecalled,
  }
}
